"use client";

import { dateFormat, formatPrice, generateCompareURLByDetailedSlugs, safeImageUrl } from '@/app/utilities/helper';
import { HeadingProps } from '@/app/utilities/interfaces';
import CustomSlider from '@/component/ui/CustomSlider/CustomSlider';
import ViewAllButton from '@/component/ui/ViewAllButton';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import ImageWrapper from './ui/ImageWrapper';
import LeadButton from './ui/Lead/LeadButton';

interface SimilarCar {
    brand: {
        _id: string;
        name: string;
        slug: string;
    };
    model: {
        _id: string;
        name: string;
        slug: string;
        image?: string;
        status?: string | null;
        launch_date?: string;
        isEV?: boolean;
        is_upcoming?: boolean;
        fuel_types?: string[];
        detailed_slug?: string;
        hasMileageRange?: boolean;
    };
    price_range: {
        base: number;
        top: number;
    };
}

interface Props {
    cars: SimilarCar[];
    headingText?: string;
    trackingClass?: string;
    linkTrackingClass?: string;
    linkSuffix?: string;
    linkLabel?: string;
    isMobile?: boolean;
    isMileagePage?: boolean;
    isImagesPage?: boolean;
    compareModelDetailedSlug?: string;
    titleTag?: HeadingProps["tag"];
    leadBtnText?: string;
    leadBtnTitle?: string;
    leadCTAHref?: string;
    showViewAllLink?: boolean;
    viewAllLinkLabel?: string;
    viewAllTrackingClass?: string;
    viewAllHref?: string;
    itemsToShow?: number;
}

export default function SimilarCarsSliderClient({ cars: initialData, headingText, trackingClass, linkTrackingClass, linkSuffix, linkLabel, isMobile, isMileagePage, isImagesPage, compareModelDetailedSlug, leadBtnText, leadBtnTitle, leadCTAHref, showViewAllLink, viewAllLinkLabel, viewAllTrackingClass, viewAllHref, titleTag = "h3", itemsToShow = 3 }: Props) {
    const [cars] = useState(initialData || []);
    const [isAtStart, setIsAtStart] = useState(true);
    const [isAtEnd, setIsAtEnd] = useState(false);

    // If no cars, render nothing on client
    if (!cars || !cars.length) return null;

    const showNavigation = cars.length > itemsToShow;

    return (
        <section id="similar-cars" className="my-8" data-testid="similar-cars-section">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-[20px] font-bold heading-p  leading-[24px] md:leading-[36px]">{headingText || 'Similar Cars'}</h2>
                    <p className="text-[14px] leading-[24px] text-gray-600 mb-6">Compare alternatives in the same price range</p>
                </div>
                {!isImagesPage && showViewAllLink !== false && (
                    <div className="ml-4 mt-1">
                        {showViewAllLink ? (
                            <ViewAllButton
                                href={viewAllHref || "/compare-cars"}
                                title={viewAllLinkLabel || 'View All Comparisons'}
                                className={`flex !rounded-[12px] !bg-[#FBFCFD] !border !border-[#EDF0F3]  !text-[#343941] !text-[12px] hidden md:inline-flex ${viewAllTrackingClass || ''}`}
                                iconClass="invert"
                            />
                        ) : (
                            <Link href={viewAllHref || "/compare-cars"} prefetch={false} className="text-[13px] font-medium text-blue-600 hover:text-blue-700 transition" title="View All Comparisons">View All Comparisons</Link>
                        )}
                    </div>
                )}
            </div>

            <div className="relative pb-[10px] md:pb-[58px]">
                <CustomSlider
                    responsive={{ mobile: 1.2, tablet: 2, desktop: itemsToShow }}
                    itemsToShow={isMobile ? 1.2 : itemsToShow}
                    gap={16}
                    showArrows={showNavigation}
                    className="product-slider"
                    onSlideChange={(idx) => {
                        setIsAtStart(idx === 0);
                        setIsAtEnd(idx >= Math.max(cars.length - itemsToShow, 0));
                    }}
                >
                    {cars.map((car) => (
                        <div key={car.model._id}><SimilarCarCard car={car} trackingClass={trackingClass} isMileagePage={isMileagePage} isImagesPage={isImagesPage}   linkTrackingClass={linkTrackingClass} linkSuffix={linkSuffix} linkLabel={linkLabel} isMobile={isMobile} compareModelDetailedSlug={compareModelDetailedSlug} leadBtnText={leadBtnText} leadBtnTitle={leadBtnTitle} leadCTAHref={leadCTAHref} titleTag={titleTag} /></div>
                    ))}
                </CustomSlider>
            </div>
        </section>
    );
}

function SimilarCarCard({ car, compact = false, trackingClass, isMileagePage, isImagesPage, linkTrackingClass, linkSuffix, linkLabel, isMobile, compareModelDetailedSlug, leadBtnText, leadBtnTitle, leadCTAHref, titleTag = "h3" }: { car: SimilarCar; compact?: boolean; trackingClass?: string; isMileagePage?: boolean; isImagesPage?: boolean; linkTrackingClass?: string; linkSuffix?: string; linkLabel?: string; isMobile?: boolean; compareModelDetailedSlug?: string; leadBtnText?: string; leadBtnTitle?: string; leadCTAHref?: string; titleTag?: HeadingProps["tag"] }) {
    const TitleTag = titleTag;
    const brand = car.brand.name;
    const model = car.model.name;
    const title = `${brand} ${model}`;
    const href = `/${car.brand.slug}/${car.model.slug}`;

    const imageUrl = safeImageUrl(car.model.image);
    const isUpcoming = car.model?.status === 'upcoming' || Boolean(car.model?.is_upcoming);
    const basePrice = car.price_range.base ? formatPrice(car.price_range.base, { includeUnit: true }) : null;
    const displayPrice = basePrice || null;

    const formattedLaunchDate = car.model?.launch_date ? dateFormat(car.model.launch_date) : "Coming Soon";
    const hasMileagePage = isMileagePage && Boolean(car.model.hasMileageRange);
    const defaultLeadCTAHref = hasMileagePage
        ? `/${car.brand.slug}/${car.model.slug}/${car.model.isEV ? "range" : "mileage"}`
        : isImagesPage
            ? `/${car.brand.slug}/${car.model.slug}/images`
            : "";

    const monthName = new Date().toLocaleString('default', { month: 'long' });

    const applyLeadTemplate = (template?: string) => {
        if (!template?.trim()) return "";
        return template
            .replace(/{brand}/g, brand)
            .replace(/{model}/g, model)
            .replace(/{Month}/g, monthName)
            .replace(/{month}/g, monthName);
    };

    const computeLeadText = () => {
        const modelName = car.model.name;
        if (leadBtnText && leadBtnText.trim()) {
            return leadBtnText
                .replace(/{brand}/g, brand)
                .replace(/{model}/g, model)
                .replace(/{Month}/g, monthName)
                .replace(/{month}/g, monthName);
        }
        if (isImagesPage) return `${modelName} Images`;
        if (hasMileagePage) return `${modelName} ${car.model.isEV ? 'Range' : 'Mileage'}`;
        if (isUpcoming) return 'Notify Me';
        // return 'Check Offers';
        return 'Check Navaratri Offers';
    };

    return (
        <div className={`bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden h-full flex flex-col mr-1 relative ${compact ? 'text-[95%]' : ''}`}>
            <Link prefetch={false} href={href} className={`block ${trackingClass}`} title={model}>
                <div className={`w-full ${compact ? 'h-40' : 'h-48'} bg-gray-50`}>
                    <ImageWrapper
                        src={imageUrl}
                        alt={`${title}`}
                        width={300}
                        height={200}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        title={`${title}`}
                    />
                    {isUpcoming && (
                        <span className="absolute inline-flex items-center gap-1 px-2 bg-[#FFF6E6] left-[8px] top-[8px] tracking-[0.4px] rounded-[8px] text-[9px] h-[22px] leading-[0] text-[#FD9C00] font-bold uppercase border border-[#FD9C00]"> Upcoming </span>
                    )}
                </div>
            </Link>
            {isUpcoming && (
                <span className='bg-[#008C8B0D] px-8 py-1 text-center font-roboto font-bold text-xs leading-4 tracking-normal'>Expected Launch <b className='text-xs leading-4 tracking-normal text-right'>{formattedLaunchDate}</b></span>
            )}

            <div className={`${compact ? 'p-3' : 'p-4'} ${isUpcoming ? '' : 'flex flex-col flex-grow'}`}>

                {isUpcoming ? (
                    <div className={`flex flex-row justify-between items-center ${isUpcoming ? 'pb-2' : ''}`}>
                        <div>
                            <div className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">
                                {brand}
                            </div>
                            <Link prefetch={false} href={href} className={`block ${trackingClass}`} aria-label={model} title={model}>
                                <TitleTag className={`${compact ? 'text-[16px]' : 'text-[18px]'} text-gray-900 mt-1 line-clamp-1 w-[92px]  md:w-[135px]`}>
                                    {model}
                                </TitleTag>
                            </Link>
                        </div>

                        <div className="text-right">
                            <div className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">Estimated Price</div>
                            {basePrice ? (
                                <p className={`${compact ? 'text-[18px]' : 'text-[16px]'} text-[#101828] mt-1 font-bold`}>{basePrice}<span className="text-[11px] text-[#62748E] font-normal ml-0.5"> onwards</span></p>
                            ) : (
                                <p className='text-[12px] text-gray-700 uppercase tracking-wider font-bold mt-1'>
                                    To be Announced
                                </p>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between gap-2">
                            <div className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">
                                {brand}
                            </div>
                        </div>
                        <Link prefetch={false} href={href} className={`block ${trackingClass}`} aria-label={model} title={model}>
                            <TitleTag className={`${compact ? 'text-[16px]' : 'text-[18px]'} text-gray-900 mt-1 line-clamp-2`}>
                                {model}
                            </TitleTag>
                        </Link>

                        {displayPrice && (
                            <p className={`${compact ? 'text-[18px]' : 'text-[20px]'} text-[#101828] mt-1`}>{displayPrice}<span className="text-[11px] text-[#62748E] font-normal ml-0.5"> onwards</span></p>
                        )}
                    </>
                )}

                <div className={`${compact ? 'mt-3' : 'mt-4'}`}>
                    <LeadButton
                        datatestid="car-lead-cta-button"
                        carId={car.model._id}
                        carName={title}
                        title={isImagesPage ? `${title} Images` : applyLeadTemplate(leadBtnTitle) || undefined}
                        btnText={
                            <span className="flex items-center justify-center gap-2">
                                {computeLeadText()}
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronRight.svg`}
                                    alt="ChevronRight Next"
                                    width={6}
                                    height={12}
                                    title="ChevronRight Next"
                                />
                            </span>
                        }
                        leadCTAHref={
                            leadCTAHref
                                ? leadCTAHref.replace(/{brand}/g, car.brand.slug).replace(/{model}/g, car.model.slug)
                                : defaultLeadCTAHref
                        }
                        is_upcoming={isUpcoming}
                        className={`w-full ${compact ? 'py-2 text-[14px]' : 'py-2.5 text-[16px]'} inline-block leading-[24px] text-white bg-[#008C8B] rounded-lg hover:opacity-90 transition cursor-pointer`}
                    />
                    {!!linkSuffix && !!linkLabel && (
                        <DetailLink href={`${href}${linkSuffix}`} text={linkLabel.replace('{brand}', brand).replace('{model}', model)} linkTrackingClass={linkTrackingClass} />
                    )}
                    {!!compareModelDetailedSlug && !!linkLabel && car.model.detailed_slug && (
                        <DetailLink
                            href={`/compare-cars/${generateCompareURLByDetailedSlugs([compareModelDetailedSlug, car.model.detailed_slug])}`}
                            text={linkLabel.replace('{brand}', brand).replace('{model}', model)}
                            linkTrackingClass={linkTrackingClass}
                        />
                    )}
                </div>

            </div>


        </div>
    );
}

const DetailLink = ({ href, text, linkTrackingClass }: { href: string; text: string, linkTrackingClass?: string }) => (
    <Link
        href={href}
        prefetch={false}
        title={text}
        className={`mt-2 text-[13px] font-medium text-blue-600 hover:text-blue-700 transition flex items-center justify-center ${linkTrackingClass || ''}`}
    >
        {text}
    </Link>
);
